/**
 * AssetLibrary - Left panel showing audio assets that can be dragged to timeline
 * Supports upload and import from other projects
 */
import { Music, Volume2, Search, Upload, FolderOpen } from 'lucide-react';
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useState, useRef } from 'react';

export function AssetLibrary({ 
  audioAssets, 
  getAssetUrl,
  onUploadAudio,
  onImportFromProject 
}) {
  const [search, setSearch] = useState('');
  const fileInputRef = useRef(null);
  
  const filteredAssets = audioAssets?.filter(asset => 
    asset.name?.toLowerCase().includes(search.toLowerCase()) ||
    asset.type?.toLowerCase().includes(search.toLowerCase())
  ) || [];

  // Handle drag start
  const handleDragStart = (e, asset) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'audio-asset',
      asset: asset
    }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  // Handle file upload
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0 && onUploadAudio) {
      onUploadAudio(files);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-3 border-b border-zinc-800">
        <h2 className="text-sm font-medium text-white flex items-center gap-2 mb-3">
          <Music className="w-4 h-4 text-indigo-400" />
          Audio Library
        </h2>
        
        {/* Action buttons */}
        <div className="flex gap-2 mb-3">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 h-8 text-xs bg-zinc-800/50 border-zinc-700 hover:bg-zinc-700 hover:text-white"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-3 h-3 mr-1" />
            Upload
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-1 h-8 text-xs bg-zinc-800/50 border-zinc-700 hover:bg-zinc-700 hover:text-white"
            onClick={onImportFromProject}
          >
            <FolderOpen className="w-3 h-3 mr-1" />
            Import
          </Button>
        </div>
        
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search audio..."
            className="h-8 pl-8 text-xs bg-zinc-800/50 border-zinc-700"
          />
        </div>
      </div>
      
      {/* Assets list */}
      <ScrollArea className="flex-1 p-3">
        {filteredAssets.length === 0 ? (
          <div className="text-center py-8 text-zinc-500 text-sm">
            <Music className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No audio files</p>
            <p className="text-xs mt-1">Upload or import audio files</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredAssets.map((asset) => (
              <div
                key={asset.id}
                draggable
                onDragStart={(e) => handleDragStart(e, asset)}
                className="p-2 bg-zinc-800/50 rounded border border-zinc-700 hover:border-indigo-500 cursor-grab active:cursor-grabbing transition-colors group"
                data-testid={`audio-asset-${asset.id}`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded flex items-center justify-center shrink-0 ${
                    asset.type === 'music' ? 'bg-purple-500/20' : 'bg-amber-500/20'
                  }`}>
                    {asset.type === 'music' ? (
                      <Music className="w-4 h-4 text-purple-400" />
                    ) : (
                      <Volume2 className="w-4 h-4 text-amber-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-white truncate group-hover:text-indigo-300 transition-colors">
                      {asset.name}
                    </p>
                    <p className="text-[10px] text-zinc-500 capitalize">{asset.type}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
      
      {/* Footer with tips */}
      <div className="p-3 border-t border-zinc-800 bg-zinc-900/50">
        <p className="text-[10px] text-zinc-600 text-center">
          Drag audio files to the timeline
        </p>
      </div>
    </div>
  );
}
